/**
 * placementEvaluation.ts — PLAT-10921 (epic decision E7).
 *
 * The pure Enforce / Accept / Rebind table. No IO, no MobX, no editor: given a
 * composition's placement, whether it is pinned, and which edge the creator
 * touched, this says what should happen to that edge.
 *
 * **These are TIMELINE pins.** A pin governs `time` and `duration` only. Canvas
 * position, scale, box size, and how long a generated text turns out to be are a
 * different axis entirely — a longer hook changes the canvas, never the
 * timeline, and can never reach this file.
 *
 * ## Cards sit OUTSIDE the clip; graphics follow its bounds
 *
 * The distinction that shapes this whole file:
 *
 * - `start` / `end` are **card** placements. An intro card plays *before* the
 *   content and an end card plays *after* it. They **define** the clip's bounds.
 * - `clip-start` / `clip-end` are **graphic** placements: pinned to where the
 *   clip's *content* starts and ends — the bounds the cards push in to. They
 *   **follow** those bounds.
 *
 * Same neighbourhood on the timeline, opposite relationship to it, so they are
 * separate values rather than one value read differently per `compositionType`.
 */

/**
 * Persisted `placement` values, as they appear in `extraElementData`.
 *
 * Deliberately duplicated from `CompositionPlacement`
 * (`src/components/sidepanel/CompositionSettings/types.ts`) rather than
 * imported: the editor domain must not depend on the components layer. The
 * duplication is pinned by a test that compares the two.
 */
export const PLACEMENT = {
  /** Card, before the clip. */
  START: 'start',
  /** Card, after the clip. */
  END: 'end',
  CURRENT_TIME: 'current_time',
  SECTION: 'section',
  /** Graphic, spanning the clip. */
  CLIP: 'clip',
  /** Graphic, pinned to the start of the clip's content. */
  CLIP_START: 'clip-start',
  /** Graphic, pinned to the end of the clip's content. */
  CLIP_END: 'clip-end',
  /** Graphic, bound to anchored words. */
  SELECTION: 'selection',
} as const;

export type PlacementValue = (typeof PLACEMENT)[keyof typeof PLACEMENT];

/** The placements that bind a timeline edge — see `isPinnablePlacement`. */
export type PinnablePlacement =
  | typeof PLACEMENT.CLIP
  | typeof PLACEMENT.CLIP_START
  | typeof PLACEMENT.CLIP_END
  | typeof PLACEMENT.SELECTION
  | typeof PLACEMENT.START
  | typeof PLACEMENT.END;

/** Which timeline edge of an element. */
export type PlacementEdge = 'start' | 'end';

/**
 * What happens to one edge.
 *
 * - `enforce` — snap the edge back to where the placement puts it, re-derived
 *   from the current reference, and clear the override flag.
 * - `accept` — the edge is free. Keep the creator's value and the override flag.
 * - `rebind` — the edge *redefines* the reference. Keep the value, rewrite the
 *   binding, stay pinned — to something new.
 */
export type EdgeOutcome = 'enforce' | 'accept' | 'rebind';

/**
 * True for the placements that bind a timeline edge at all.
 *
 * `current_time` and `section` describe only where a graphic is *inserted*;
 * nothing about them survives the insert, so a `placementPinned: true` on one of
 * them has nothing to enforce. This predicate is what keeps every pre-existing
 * `current_time` record inert under the new reconciler.
 */
export function isPinnablePlacement(
  placement: string | null | undefined,
): placement is PinnablePlacement {
  return (
    placement === PLACEMENT.CLIP ||
    placement === PLACEMENT.CLIP_START ||
    placement === PLACEMENT.CLIP_END ||
    placement === PLACEMENT.SELECTION ||
    placement === PLACEMENT.START ||
    placement === PLACEMENT.END
  );
}

/**
 * True for the two placements that make a composition a scope-defining CARD.
 *
 * A card that defines a bound cannot be pinned to a scope derived from that
 * bound — see `derivePinnedTimelineTarget`, which refuses the combination.
 */
export function isCardPlacement(placement: string | null | undefined): boolean {
  return placement === PLACEMENT.START || placement === PLACEMENT.END;
}

/**
 * True when a placement's TRANSCRIPT range is the anchored selection.
 *
 * `clip-start` / `clip-end` enforce one edge from the clip's bounds and take the
 * other from the selection, so the words they cover run from the clip's start to
 * the end of that selection — WIDER than the passage the creator highlighted.
 * Their anchor still records that passage, but honouring it for the underline
 * would draw the highlight back down to it and contradict the graphic sitting on
 * the timeline. The time window is the honest range for those two, and the
 * reconciler is what keeps the bound edge in it.
 *
 * Every other placement keeps the anchor, which is what holds a `selection`
 * graphic on the same words through ripple edits (see `findOverlayRanges`).
 */
export function placementUsesTranscriptAnchor(
  placement: string | null | undefined,
): boolean {
  return placement !== PLACEMENT.CLIP_START && placement !== PLACEMENT.CLIP_END;
}

export interface EvaluatePlacementEdgeParams {
  placement: string | null | undefined;
  edge: PlacementEdge;
  /** `placementPinOverride ?? placementPinned ?? false` — see `readPlacementMeta`. */
  pinned: boolean;
}

/**
 * The table, one edge at a time. Edges are evaluated independently — a
 * `clip-end` graphic dragged longer at its start keeps that (Accept) while a
 * change to its end snaps back (Enforce).
 *
 * | Placement                | Start edge | End edge |
 * | ------------------------ | ---------- | -------- |
 * | `clip`                   | Enforce    | Enforce  |
 * | `clip-start`             | Enforce    | Accept   |
 * | `clip-end`               | Accept     | Enforce  |
 * | `start`  (intro card)    | Enforce    | Accept   |
 * | `end`    (end card)      | Enforce    | Accept   |
 * | `selection`              | Rebind     | Rebind   |
 * | *any, not pinned*        | Accept     | Accept   |
 *
 * Note `start` and `end` agree: a card's pinned edge is its **start** (an end
 * card is positioned by where it begins playing, its duration left alone).
 */
export function evaluatePlacementEdge({
  placement,
  edge,
  pinned,
}: EvaluatePlacementEdgeParams): EdgeOutcome {
  if (!pinned || !isPinnablePlacement(placement)) return 'accept';

  switch (placement) {
    case PLACEMENT.CLIP:
      // The clip's bounds define both edges, so both re-derive.
      return 'enforce';

    case PLACEMENT.SELECTION:
      // The anchored words are the source of truth and the time range is
      // derived from them (E4), so a moved edge means a different passage —
      // never a violated pin.
      return 'rebind';

    case PLACEMENT.CLIP_END:
      // Ends WITH the clip's content; where it starts is the creator's business.
      return edge === 'end' ? 'enforce' : 'accept';

    case PLACEMENT.CLIP_START:
    case PLACEMENT.START:
    case PLACEMENT.END:
      // All three are positioned by their START and keep their own duration: a
      // `clip-start` graphic begins with the content, an intro card begins at the
      // timeline start, an end card begins where the content stops.
      return edge === 'start' ? 'enforce' : 'accept';
  }
}
